import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, BookmarkCheck, FileBarChart, FileText, Cpu, Loader2, UserCheck, Sparkles, CalendarDays } from "lucide-react";
import { api, formatApiError, trackStage, trackEvent, getSid } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, DemoRibbon } from "../components/StatusBadge";
import ExtractionPanel from "../components/ExtractionPanel";
import LoginPrompt from "../components/LoginPrompt";
import InstitutionalCta from "../components/InstitutionalCta";
import UpgradePrompt from "../components/UpgradePrompt";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const CERT_COLOR = { low: "pending", medium: "medium", high: "high" };
const RISK_COLOR = { low: "low", medium: "medium", elevated: "elevated", high: "pending" };

// Visible, persistent report-failure state with retry — never a bare toast
// (owner requirement 2026-09-17: no silent failures in the report flow).
import { KiedysDzis } from "../components/KiedysDzis";
function ReportErrorBox({ kind, onRetry, t }) {
  return (
    <div data-testid="report-error-box" role="alert"
         className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-rose-300 bg-rose-50 p-4">
      <p data-testid="report-error-text" className="text-sm text-rose-800">
        {kind === "source" ? t("reports.error.source") : t("reports.error.generic")}
      </p>
      <Button data-testid="report-error-retry" variant="outline" size="sm" onClick={onRetry}>
        {t("reports.error.retry")}
      </Button>
    </div>
  );
}

export default function ParcelPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hydro, setHydro] = useState(undefined);
  const [saved, setSaved] = useState(false);
  const [extractions, setExtractions] = useState({});
  const [analyzing, setAnalyzing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [reportError, setReportError] = useState(null);

  useEffect(() => {
    // Real TERYT ids contain "/" — they travel via the resolve query-param endpoint
    // (path-encoded slashes don't survive ASGI routing/proxies); "~" is the URL-safe form.
    const realId = id.replaceAll("~", "/");
    const req = realId.includes("/")
      ? api.get("/parcels/resolve", { params: { id: realId } })
      : api.get(`/parcels/${id}`);
    req.then((r) => {
      setData(r.data);
      trackStage("funnel_parcel_view");
      setExtractions(r.data.extractions || {});
      // PG-HYDRO-001: lazy progressive load — hydrology never blocks the page.
      if (!r.data.demo) {
        setHydro(null);
        api.get("/parcels/hydrology", { params: { id: realId } })
          .then((h) => { setHydro(h.data); trackEvent("hydrology_loaded"); })
          .catch(() => setHydro(false));
      }
    }).catch(() => setData(false));
  }, [id]);

  useEffect(() => {
    if (!user) { setSaved(false); return; }
    api.get("/saved-parcels").then((r) => {
      setSaved(r.data.results.some((s) => s.parcel_id === id));
    }).catch(() => {});
  }, [user, id]);

  if (data === null) return <div className="p-20 text-center text-slate-500">{t("common.loading")}</div>;
  if (data === false) return <div className="p-20 text-center text-slate-500">404</div>;
  if (data.data_kind === "official") return <RealParcelView data={data} hydro={hydro} t={t} lang={lang} />;

  const { parcel, gmina, documents, consultations } = data;

  const saveParcel = async () => {
    if (!user) { setPrompt(t("prompt.save")); return; }
    try {
      await api.post("/saved-parcels", { parcel_id: parcel.id });
      setSaved(true);
      toast.success(t("parcel.saved"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateReport = async () => {
    if (!user) { navigate(`/raport/podglad/${parcel.id}`); return; }
    setGenerating(true);
    setReportError(null);
    try {
      const { data: r } = await api.post("/reports", { parcel_id: parcel.id }, { timeout: 90000 });
      if (!r?.id) throw new Error("malformed report response");
      navigate(`/raport/${r.id}`);
    } catch (e) {
      setReportError(e?.response?.status === 503 ? "source" : "generic");
      setGenerating(false);
    }
  };

  const analyze = async (docId) => {
    if (!user) { setPrompt(t("prompt.ai")); return; }
    setAnalyzing(docId);
    try {
      const { data: ex } = await api.post(`/documents/${docId}/analyze`);
      setExtractions((prev) => ({ ...prev, [docId]: ex }));
      toast.success("OK");
    } catch (e) { toast.error(formatApiError(e)); }
    setAnalyzing(null);
  };

  const statusCards = [
    { label: t("parcel.planOgolny"), status: parcel.plan_ogolny_status, text: t(`status.${parcel.plan_ogolny_status}`), testId: "status-card-plan-ogolny" },
    { label: t("parcel.mpzp"), status: parcel.mpzp_status, text: t(`mpzp.${parcel.mpzp_status}`), testId: "status-card-mpzp" },
    { label: t("parcel.proceedings"), status: parcel.proceedings_status, text: t(`proc.${parcel.proceedings_status}`), testId: "status-card-proceedings" },
    { label: t("parcel.consultation"), status: parcel.consultation_status, text: t(`cons.${parcel.consultation_status}`), testId: "status-card-consultation" },
  ];

  return (
    <div data-testid="parcel-intelligence-page" className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="break-all font-mono-data text-xl font-bold tracking-tight sm:text-2xl">{parcel.full_number}</h1>
            <DemoRibbon label={t("demo.label")} />
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {parcel.address} · {t("common.gmina")}{" "}
            <Link to={`/gmina/${gmina.id}`} data-testid="parcel-gmina-link" className="font-semibold text-[#DC143C] hover:underline">
              {gmina.name}
            </Link>{" "}
            · {t("common.powiat")} {parcel.powiat} · {t("common.woj")} {parcel.wojewodztwo}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">
            {t("parcel.area")}: {parcel.area_m2.toLocaleString()} m² · {parcel.coordinates.lat}, {parcel.coordinates.lng}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="save-parcel-button"
            variant={saved ? "secondary" : "outline"}
            onClick={saveParcel}
          >
            {saved ? <BookmarkCheck size={16} className="mr-2 text-emerald-600" /> : <Bookmark size={16} className="mr-2" />}
            {saved ? t("parcel.saved") : t("parcel.save")}
          </Button>
          <Button
            data-testid="generate-report-button"
            className="bg-[#DC143C] text-white hover:bg-[#B91C1C]"
            onClick={generateReport}
            disabled={generating}
          >
            <FileBarChart size={16} className="mr-2" />
            {generating ? (<><Loader2 size={15} className="mr-2 animate-spin" />{t("common.loading")}</>) : t("reports.button")}
          </Button>
        </div>
      </div>

      {reportError && <ReportErrorBox kind={reportError} onRetry={generateReport} t={t} />}

      <div data-testid="parcel-intelligence-status-card" className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statusCards.map((c) => (
          <div key={c.testId} data-testid={c.testId} className="rounded-xl border bg-white p-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{c.label}</p>
            <div className="mt-2"><StatusBadge status={c.status} label={c.text} /></div>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("parcel.certainty")}</p>
          <div className="mt-2"><StatusBadge status={CERT_COLOR[parcel.development_certainty]} label={t(`cert.${parcel.development_certainty}`)} testId="certainty-badge" /></div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("parcel.risk")}</p>
          <div className="mt-2"><StatusBadge status={RISK_COLOR[parcel.change_risk]} label={t(`risk.${parcel.change_risk}`)} testId="risk-badge" /></div>
        </div>
      </div>

      <p className="mt-4 rounded-lg border bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <span className="font-semibold">{t("parcel.zone")}:</span> {parcel.planning_zone}
        {parcel.mpzp_name && <span className="block mt-1 text-xs text-slate-500">{parcel.mpzp_name}</span>}
      </p>

      <Tabs defaultValue="facts" className="mt-8">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="facts" data-testid="layer-source-facts-tab">
            <FileText size={14} className="mr-1.5" />{t("evidence.facts")}
          </TabsTrigger>
          <TabsTrigger value="ai" data-testid="layer-ai-interpretation-tab">
            <Cpu size={14} className="mr-1.5" />{t("evidence.ai")}
          </TabsTrigger>
          <TabsTrigger value="human" data-testid="layer-human-review-tab">
            <UserCheck size={14} className="mr-1.5" />{t("evidence.human")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="facts">
          <div className="overflow-hidden rounded-xl border bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-left text-xs uppercase tracking-wide text-white">
                <tr>
                  <th className="px-4 py-3">Fakt / Fact</th>
                  <th className="px-4 py-3">Wartość / Value</th>
                  <th className="hidden px-4 py-3 md:table-cell">{t("report.source")}</th>
                  <th className="hidden px-4 py-3 lg:table-cell">{t("report.effective")}</th>
                  <th className="hidden px-4 py-3 lg:table-cell">{t("report.verification")}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {parcel.facts.map((f, i) => (
                  <tr key={i} data-testid={`fact-row-${i}`} className="align-top">
                    <td className="px-4 py-3 font-medium text-slate-900">{lang === "pl" ? f.label_pl : f.label_en}</td>
                    <td className="px-4 py-3 text-slate-700">{lang === "pl" ? f.value_pl : f.value_en}</td>
                    <td className="hidden px-4 py-3 text-xs text-slate-500 md:table-cell">{f.source_authority}</td>
                    <td className="hidden px-4 py-3 text-xs text-slate-500 lg:table-cell">{f.effective_date}</td>
                    <td className="hidden px-4 py-3 lg:table-cell">
                      <span className={`text-xs font-semibold ${f.verification_status === "zweryfikowane" ? "text-emerald-700" : "text-amber-700"}`}>
                        {f.verification_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="ai">
          <div data-testid="ai-interpretation-panel" className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-700">{t("evidence.ai")}</p>
            <p className="mt-3 text-base leading-relaxed text-slate-800">
              {lang === "pl" ? parcel.ai_interpretation.text_pl : parcel.ai_interpretation.text_en}
            </p>
            <p className="mt-4 text-xs text-slate-500">
              {t("evidence.ai.note")} · model: {parcel.ai_interpretation.model}
            </p>
          </div>
        </TabsContent>

        <TabsContent value="human">
          <div data-testid="human-review-panel" className="rounded-xl border border-rose-200 bg-rose-50/40 p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-rose-700">{t("evidence.human")}</p>
            <p className="mt-3 text-sm text-slate-700">{t("evidence.human.pending")}</p>
            <Button data-testid="human-review-request-button" variant="outline" className="mt-4 border-rose-300 text-rose-700 hover:bg-rose-100">
              <UserCheck size={15} className="mr-2" /> {t("evidence.human.cta")}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <section>
          <h2 className="font-heading text-lg font-semibold">{t("parcel.documents")}</h2>
          <div className="mt-3 space-y-3">
            {documents.map((d) => (
              <div key={d.id} data-testid={`document-card-${d.id}`} className="rounded-xl border bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{d.title}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {d.authority} · {d.publication_date} · <span className="font-semibold uppercase">{d.status}</span>
                    </p>
                  </div>
                  <Button
                    data-testid={`analyze-doc-${d.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => analyze(d.id)}
                    disabled={analyzing === d.id}
                    className="shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  >
                    <Sparkles size={13} className="mr-1.5" />
                    {analyzing === d.id ? t("ai.analyzing") : t("ai.analyze")}
                  </Button>
                </div>
                <ExtractionPanel extraction={extractions[d.id]} t={t} lang={lang} />
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold">{t("parcel.consultations")}</h2>
          <div className="mt-3 space-y-3">
            {consultations.length === 0 && <p className="text-sm text-slate-500">—</p>}
            {consultations.map((c) => (
              <div key={c.id} data-testid={`consultation-card-${c.id}`} className="flex items-center justify-between rounded-xl border bg-white p-4">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{c.document_title}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                    <CalendarDays size={12} /> {c.start_date} → {c.end_date}
                  </p>
                </div>
                <StatusBadge status={c.status} label={t(`cons.${c.status}`)} testId={`consultation-status-${c.id}`} />
              </div>
            ))}
          </div>
        </section>
      </div>
      <LoginPrompt open={!!prompt} onOpenChange={() => setPrompt(null)} message={prompt} />
    </div>
  );
}

function RealParcelView({ data, hydro, t, lang }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hydroOpen, setHydroOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [upgrade, setUpgrade] = useState(null);
  const [prompt, setPrompt] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const p = data.parcel;

  // Conversion CTAs are shown only after a real parcel was successfully viewed.
  useEffect(() => {
    trackEvent("save_parcel_cta_view");
    trackEvent("planning_watch_cta_view");
  }, []);

  // Save intent survives the auth round-trip: count the post-registration return once.
  useEffect(() => {
    try {
      if (user && sessionStorage.getItem("pg_pending_save") === p.id) {
        sessionStorage.removeItem("pg_pending_save");
        sessionStorage.setItem("pg_post_reg", p.id);
        setResumed(true);
        trackEvent("save_parcel_after_registration");
      }
    } catch { /* private mode */ }
  }, [user, p.id]);
  const saveReal = async (countClick = true) => {
    if (countClick) trackEvent("save_parcel_cta_click");
    if (!user) {
      try { sessionStorage.setItem("pg_pending_save", p.id); } catch { /* private mode */ }
      setPrompt(true);
      return;
    }
    setSaving(true);
    try {
      await api.post("/saved-parcels", { parcel_id: p.id, sid: getSid() });
      try {
        localStorage.setItem("pg_ret_s", "1");
        localStorage.setItem("pg_ret_w", "1");
        if (sessionStorage.getItem("pg_post_reg") === p.id) {
          sessionStorage.removeItem("pg_post_reg");
          trackEvent("save_after_reg_success");
        }
      } catch { /* private mode */ }
      setSaved(true);
      trackStage("funnel_save");
      trackStage("funnel_watch");
      try {
        const ref = sessionStorage.getItem("pg_ref") || sessionStorage.getItem("pg_demo_ref");
        if (ref) api.post("/analytics/event", { event: "parcel_save", ref }).catch(() => {});
      } catch { /* private mode */ }
      toast.success(t("parcel.saved"));
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && d?.code) {
        setUpgrade(d);
        trackEvent("upgrade_prompt_view", { ref: d.code });
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setSaving(false); }
  };
  const [reportError, setReportError] = useState(null);
  const [reportLocked, setReportLocked] = useState(null);
  const reportReal = async () => {
    // Anonymous users get the public preview; signed-in users get the full report.
    if (!user) { navigate(`/raport/podglad/${p.id.replaceAll("/", "~")}`); return; }
    setGenerating(true);
    setReportError(null);
    setReportLocked(null);
    try {
      const { data: r } = await api.post("/reports", { parcel_id: p.id }, { timeout: 90000 });
      if (!r?.id) throw new Error("malformed report response");
      navigate(`/raport/${r.id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && d?.code === "report_locked") {
        // Same unlock UX as ReportPreviewPage: visible card, never a bare toast.
        setReportLocked(d);
        trackEvent("upgrade_prompt_view", { ref: "report_locked" });
      } else {
        setReportError(e?.response?.status === 503 ? "source" : "generic");
      }
      setGenerating(false);
    }
  };
  const watchClick = async () => {
    trackEvent("planning_watch_cta_click");
    if (!user) {
      try { sessionStorage.setItem("pg_pending_save", p.id); } catch { /* private mode */ }
      setPrompt(true);
      return;
    }
    if (!saved) await saveReal(false);  // Planning Watch activates together with the save
  };
  const planning = data.planning || {};
  const pog = planning.plan_ogolny || {};
  const mpzp = planning.mpzp || {};
  const pogStatus = pog.status === "adopted" ? "adopted" : pog.status === "draft" ? "draft" : "unknown";
  const srcLine = (s) => s ? `${s.authority} · ${t("real.retrieved")}: ${(s.retrieved_at || "").slice(0, 19).replace("T", " ")}` : null;
  return (
    <div data-testid="real-parcel-page" className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="break-all font-mono-data text-xl font-bold tracking-tight sm:text-2xl">{p.full_number}</h1>
        <span data-testid="official-data-badge" className="rounded border border-emerald-400 bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
          {t("data.official")}
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-600">
        {t("common.gmina")} {p.gmina_name} · {t("common.powiat")} {p.powiat} · {t("common.woj")} {p.wojewodztwo}
        {p.obreb ? ` · ${t("real.obreb")} ${p.obreb}` : ""}
      </p>
      {p.centroid && (
        <p className="mt-0.5 text-xs text-slate-400">
          {t("real.centroid")}: {p.centroid.lat.toFixed(6)}, {p.centroid.lon.toFixed(6)}
        </p>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div data-testid="real-pog-card" className="rounded-xl border bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("parcel.planOgolny")}</p>
          <div className="mt-2"><StatusBadge status={pogStatus} label={t(`status.${pogStatus}`)} testId="real-pog-status" /></div>
          {srcLine(pog.source) && <p className="mt-2 text-xs text-slate-500">{srcLine(pog.source)}</p>}
        </div>
        <div data-testid="real-mpzp-card" className="rounded-xl border bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("parcel.mpzp")}</p>
          <div className="mt-2">
            <StatusBadge
              status={mpzp.status === "existing" ? "existing" : "unknown"}
              label={mpzp.status === "existing" ? t("mpzp.existing") : t("data.none")}
              testId="real-mpzp-status"
            />
          </div>
          {mpzp.plans?.length > 0 && <p className="mt-2 text-xs text-slate-700">{mpzp.plans.join("; ")}</p>}
          {srcLine(mpzp.source) && <p className="mt-2 text-xs text-slate-500">{srcLine(mpzp.source)}</p>}
        </div>
      </div>

      {(hydro !== undefined || data.flood) && (
        <div data-testid="hydro-card" className="mt-4 rounded-xl border bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("hydro.title")}</p>
          {hydro === null && <p className="mt-2 text-sm text-slate-500" data-testid="hydro-loading">{t("hydro.loading")}</p>}
          {hydro === false && data.flood && (
            <div className="mt-2">
              <StatusBadge status={data.flood.status} label={lang === "pl" ? data.flood.badge_pl : data.flood.badge_en} testId="flood-badge" />
            </div>
          )}
          {hydro && (
            <div className="mt-2 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span data-testid="hydro-level" className="inline-block rounded-full px-3 py-1 text-xs font-bold bg-slate-900 text-white">
                  {t("hydro.exposure")}: {lang === "pl" ? hydro.summary.level_pl : hydro.summary.level_en}
                </span>
                {hydro.exposure && (
                  <span data-testid="hydro-exposure" className={`inline-block rounded-full border px-3 py-1 text-xs font-bold ${
                    { ON_PARCEL: "border-rose-300 bg-rose-50 text-rose-800",
                      NEARBY: "border-amber-300 bg-amber-50 text-amber-800",
                      NOT_IN_SET: "border-emerald-300 bg-emerald-50 text-emerald-800" }[hydro.exposure.state] || "border-slate-300 bg-slate-50 text-slate-700"}`}>
                    {lang === "pl" ? hydro.exposure.badge_pl : hydro.exposure.badge_en}
                  </span>
                )}
                {hydro.flood_badge && !hydro.exposure && (
                  <StatusBadge status={hydro.flood_badge.status} label={lang === "pl" ? hydro.flood_badge.badge_pl : hydro.flood_badge.badge_en} testId="flood-badge" />
                )}
              </div>
              <div data-testid="hydro-reasons">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("hydro.why")}</p>
                <ul className="mt-1 list-disc pl-4 text-sm text-slate-700 space-y-1">
                  {hydro.summary.reasons.map((r, i) => <li key={i} data-testid={`hydro-reason-${i}`}>{r}</li>)}
                </ul>
              </div>
              {(hydro.flood?.flood_findings || []).map((f, i) => (
                <div key={i} data-testid={`hydro-finding-${i}`} className="rounded-lg bg-slate-50 p-3 text-sm">
                  <p className="font-semibold text-slate-800">{f.layer_pl}</p>
                  <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1">
                    <div><dt className="text-xs text-slate-500">{t("hydro.affected_area")}</dt><dd className="font-semibold" data-testid={`hydro-finding-area-${i}`}>{Math.round(f.intersection_area_m2).toLocaleString()} m²</dd></div>
                    <div><dt className="text-xs text-slate-500">{t("hydro.affected_pct")}</dt><dd className="font-semibold" data-testid={`hydro-finding-pct-${i}`}>{f.intersection_percent}%</dd></div>
                    {f.scenario_oficjalny && <div className="col-span-2"><dt className="text-xs text-slate-500">{t("hydro.scenario")}</dt><dd data-testid={`hydro-finding-scenario-${i}`}>{f.scenario_oficjalny}</dd></div>}
                  </dl>
                </div>
              ))}
              {hydro.water_summary?.intersects_parcel > 0 && (
                <p className="text-sm font-semibold text-slate-800" data-testid="hydro-water-crosses">{t("hydro.crosses")}</p>
              )}
              {hydro.water_summary?.nearest_water_m != null && hydro.water_summary?.intersects_parcel === 0 && (
                <p className="text-sm text-slate-700" data-testid="hydro-water-nearest">{t("hydro.nearest_water")}: {hydro.water_summary.nearest_water_m} m</p>
              )}
              {hydro.catchment?.nazwy?.length > 0 && (
                <p className="text-sm text-slate-700" data-testid="hydro-catchment">{t("hydro.catchment")}: {hydro.catchment.nazwy.join(", ")}</p>
              )}
              {hydro.imgw_context?.status === "OK" && hydro.imgw_context.station && (
                <div className="text-sm text-slate-700" data-testid="hydro-imgw">
                  <p>{t("hydro.station")}: {hydro.imgw_context.station.nazwa}{hydro.imgw_context.station.rzeka ? ` (${hydro.imgw_context.station.rzeka})` : ""} — {hydro.imgw_context.station.odleglosc_km} km</p>
                  {hydro.imgw_context.station.stan_wody_cm != null && (
                    <p className="text-xs text-slate-500">{hydro.imgw_context.station.stan_wody_cm} cm · {hydro.imgw_context.station.pomiar_at}</p>
                  )}
                  <p className="text-xs italic text-slate-500">{t("hydro.context_note")}</p>
                </div>
              )}
              {hydro.imgw_context?.warnings?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="hydro-warnings">
                  <p className="font-semibold">{t("hydro.warnings")}</p>
                  {hydro.imgw_context.warnings.map((w, i) => (
                    <p key={i} className="mt-1">{w.zdarzenie} — {lang === "pl" ? "stopień" : "level"} {w.stopien} ({w.data_od} → {w.data_do})</p>
                  ))}
                </div>
              )}
              {(hydro.flood?.source_status === "TEMPORARILY_UNAVAILABLE" || hydro.imgw_context?.status === "SOURCE_UNAVAILABLE") && (
                <p className="text-xs text-slate-500" data-testid="hydro-source-note">{t("hydro.unavailable")}</p>
              )}
              <p className="mt-3 text-xs text-slate-500" data-testid="hydro-disclaimer">{hydro.summary.disclaimer_pl}</p>
              {hydro.exposure && (
                <p className="text-xs text-slate-500" data-testid="hydro-exposure-caveat">{hydro.exposure.caveat_pl}</p>
              )}
              <button type="button" data-testid="hydro-details-toggle" onClick={() => { if (!hydroOpen) trackEvent("hydrology_detail_opened"); setHydroOpen(!hydroOpen); }}
                className="text-xs font-semibold text-slate-600 underline underline-offset-2">
                {hydroOpen ? t("hydro.details.hide") : t("hydro.details.show")}
              </button>
              {hydroOpen && (
                <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600 space-y-2" data-testid="hydro-details">
                  {hydro.sources?.filter(Boolean).map((p, i) => (
                    <div key={i} data-testid={`hydro-source-${i}`}>
                      <p className="font-semibold">{p.authority}</p>
                      <p>{p.dataset}</p>
                      <p>{lang === "pl" ? "Pobrano" : "Retrieved"}: {p.retrieved_at}</p>
                      {p.method && <p className="mt-1">{p.method}</p>}
                      {p.limitations && <p className="mt-1 italic">{p.limitations}</p>}
                    </div>
                  ))}
                  <p data-testid="hydro-version">{hydro.analysis_version} · {hydro.generated_at}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {data.heritage && (
        <div data-testid="heritage-badge-card" className="mt-4 rounded-xl border bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("heritage.title")}</p>
          <div className="mt-2">
            <StatusBadge
              status={data.heritage.status}
              label={lang === "pl" ? data.heritage.badge_pl : data.heritage.badge_en}
              testId="heritage-badge"
            />
          </div>
        </div>
      )}

      {data.elevation && (
        <div data-testid="elevation-badge-card" className="mt-4 rounded-xl border bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("elevation.title")}</p>
          <div className="mt-2">
            <StatusBadge
              status={data.elevation.status === "DETECTED" ? "unknown" : data.elevation.status}
              label={lang === "pl" ? data.elevation.badge_pl : data.elevation.badge_en}
              testId="elevation-badge"
            />
          </div>
        </div>
      )}

      {data.soils && (
        <div data-testid="soils-badge-card" className="mt-4 rounded-xl border bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("soils.title")}</p>
          <div className="mt-2">
            <StatusBadge
              status={data.soils.status === "DETECTED" ? "unknown" : data.soils.status}
              label={lang === "pl" ? data.soils.badge_pl : data.soils.badge_en}
              testId="soils-badge"
            />
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          data-testid="save-parcel-button"
          variant={saved ? "outline" : "default"}
          className={saved ? "" : "bg-slate-900 text-white hover:bg-slate-800"}
          onClick={saveReal}
        >
          <Bookmark size={15} className="mr-2" />
          {saved ? t("parcel.saved") : t("parcel.save")}
        </Button>
        <Button
          data-testid="real-preview-button"
          className="bg-[#DC143C] text-white hover:bg-[#B91C1C]"
          onClick={reportReal}
          disabled={generating}
        >
          <FileBarChart size={15} className="mr-2" />
          {generating ? (<><Loader2 size={15} className="mr-2 animate-spin" />{t("common.loading")}</>) : t("reports.button")}
        </Button>
      </div>

      {reportLocked && (
        <div data-testid="report-locked-card" className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-6">
          <p className="font-heading text-base font-bold text-slate-900">{t("report.upgrade.title")}</p>
          <p className="mt-1 text-sm text-slate-600">{t("report.upgrade.text")}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              data-testid="parcel-unlock-report-single"
              className="bg-[#DC143C] text-white hover:bg-[#B91C1C]"
              onClick={() => { trackEvent("upgrade_prompt_click", { ref: "report_locked" }); navigate(`/platnosc/raport/${p.id.replaceAll("/", "~")}?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`); }}
            >
              {t("report.upgrade.cta")}
            </Button>
            <Button
              data-testid="parcel-unlock-report-pro"
              variant="outline"
              onClick={() => { trackEvent("upgrade_prompt_click", { ref: "report_pro" }); navigate("/platnosc/plan/pro"); }}
            >
              {t("report.upgrade.cta_pro")}
            </Button>
          </div>
        </div>
      )}
      {reportError && <ReportErrorBox kind={reportError} onRetry={reportReal} t={t} />}

      <KiedysDzis parcelId={p.id} lang={lang} />

      <h2 className="mt-8 font-heading text-lg font-semibold">{t("evidence.facts")}</h2>
      <div className="mt-3 overflow-hidden rounded-xl border bg-white">
        <table data-testid="real-evidence-table" className="w-full text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase tracking-wide text-white">
            <tr>
              <th className="px-4 py-3">Fakt / Fact</th>
              <th className="px-4 py-3">Wartość / Value</th>
              <th className="hidden px-4 py-3 md:table-cell">{t("report.source")}</th>
              <th className="hidden px-4 py-3 lg:table-cell">{t("real.retrieved")}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {(data.evidence || []).map((f, i) => (
              <tr key={i} data-testid={`real-fact-row-${i}`} className="align-top">
                <td className="px-4 py-3 font-medium text-slate-900">{lang === "pl" ? f.fact_pl : f.fact_en}</td>
                <td className="px-4 py-3 text-slate-700">{lang === "pl" ? f.value_pl : f.value_en}</td>
                <td className="hidden px-4 py-3 text-xs text-slate-500 md:table-cell">
                  {f.source_url ? (
                    <a href={f.source_url} target="_blank" rel="noreferrer" className="text-[#DC143C] hover:underline">
                      {f.source_authority}
                    </a>
                  ) : f.source_authority}
                </td>
                <td className="hidden px-4 py-3 text-xs text-slate-500 lg:table-cell">{(f.retrieved_at || "").slice(0, 19).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <section
        data-testid="track-parcel-cta"
        className={`mt-10 grid gap-6 rounded-xl border p-6 sm:grid-cols-2 ${
          resumed && !saved ? "border-[#DC143C] bg-rose-50/40 ring-1 ring-[#DC143C]" : "border-slate-200 bg-slate-50"
        }`}
      >
        <div>
          <h2 className="font-heading text-base font-bold text-slate-900">
            {saved ? t("conv.saved_confirm") : t("conv.title")}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">
            {saved ? t("watch.active_text") : t("conv.text")}
          </p>
          {resumed && !saved && (
            <p data-testid="save-resume-note" className="mt-2 text-xs font-semibold text-[#DC143C]">
              {t("conv.resumed")}
            </p>
          )}
          {!saved && (
            <Button
              data-testid="save-parcel-cta-button"
              className="mt-3 bg-slate-900 text-white hover:bg-slate-800"
              onClick={() => saveReal()}
              disabled={saving}
            >
              <Bookmark size={15} className="mr-2" />
              {t("conv.save")}
            </Button>
          )}
          {saved && (
            <span data-testid="save-confirmation" className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
              ✓ {t("parcel.saved")}
            </span>
          )}
        </div>
        <div className="sm:border-l sm:border-slate-200 sm:pl-6">
          <h2 className="font-heading text-base font-bold text-slate-900">{t("watch.title")}</h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{t("watch.text")}</p>
          <Button data-testid="planning-watch-cta-button" variant="outline" className="mt-3" onClick={watchClick} disabled={saved}>
            {saved ? t("watch.active") : t("watch.cta")}
          </Button>
        </div>
      </section>

      {upgrade && (
        <UpgradePrompt code={upgrade.code} limit={upgrade.limit} current={upgrade.current}
                       onClose={() => setUpgrade(null)} />
      )}

      <div className="mt-6">
        <InstitutionalCta variant="card" />
      </div>

      <LoginPrompt open={prompt} onOpenChange={setPrompt} message={t("prompt.save")} />
    </div>
  );
}
